import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Save, User, Building2, Phone, Mail, MapPin,
  Briefcase, Clock, Shield, Star, CheckCircle, AlertCircle, Award
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { BadgeSection } from "../components/ContractorBadges";

// Available skills/categories
const AVAILABLE_SKILLS = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Painting",
  "Roofing",
  "Flooring",
  "Carpentry",
  "Landscaping",
  "Handyman / General Repairs",
  "Cleaning",
  "Moving / Hauling",
  "Remodel / Restoration",
  "Countertops",
  "Tile",
  "Appliance Repair",
  "Drywall",
  "Pressure Washing",
  "Pest Control"
];

// Service areas (Austin area counties - alphabetical order)
const AUSTIN_COUNTIES = [
  "Bastrop",
  "Hays",
  "Travis",
  "Williamson"
];

// Job sizes
const JOB_SIZES = [
  "Less than $1,000",
  "$1,000-$5,000",
  "$5,000-$10,000",
  "$10,000-$15,000",
  "$15,000-$25,000",
  "$25,000+"
];

// Availability options
const AVAILABILITY_OPTIONS = [
  { value: "same_day", label: "Same-day / Emergency" },
  { value: "1-3_days", label: "Within 1-3 days" },
  { value: "1_week", label: "Within 1 week" },
  { value: "flexible", label: "Flexible / By appointment" }
];

export default function ContractorProfileEdit() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: "",
    full_name: "",
    phone: "",
    bio: "",
    skills: [],
    service_areas: [],
    years_experience: "",
    is_licensed: false,
    is_insured: false,
    job_size_accepted: [],
    availability: [],
    preferred_contact_method: "phone",
    review_source: "none",
    review_url: ""
  });

  useEffect(() => {
    if (!user || user.role !== "contractor") {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/contractor/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setProfile(data);
      
      // Populate form with existing data
      setFormData({
        business_name: data.business_name || "",
        full_name: data.full_name || "",
        phone: data.phone || "",
        bio: data.bio || "",
        skills: data.skills || [],
        service_areas: data.service_areas || [],
        years_experience: data.years_experience || "",
        is_licensed: data.is_licensed === "Yes" || data.is_licensed === true,
        is_insured: data.is_insured === true || data.is_insured === "Yes",
        job_size_accepted: data.job_size_accepted || [],
        availability: data.availability || [],
        preferred_contact_method: data.preferred_contact_method || "phone",
        review_source: data.review_source || "none",
        review_url: data.review_url || "",
        // Badge system fields (read-only display)
        badges_earned: data.badges_earned || [],
        metrics_snapshot: data.metrics_snapshot || {}
      });
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => {
      const current = prev[field] || [];
      const updated = current.includes(item)
        ? current.filter(i => i !== item)
        : [...current, item];
      return { ...prev, [field]: updated };
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (formData.skills.length === 0) {
      toast.error("Please select at least one skill");
      return;
    }
    if (formData.service_areas.length === 0) {
      toast.error("Please select at least one service area");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/contractor/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully!");
      // Refresh profile data
      await fetchProfile();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      let errorMessage = "Failed to save profile";
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Calculate profile completeness
  const calculateCompleteness = () => {
    let score = 0;
    if (formData.business_name) score += 15;
    if (formData.phone) score += 10;
    if (formData.bio && formData.bio.length > 50) score += 15;
    if (formData.skills.length > 0) score += 15;
    if (formData.service_areas.length > 0) score += 10;
    if (formData.years_experience) score += 10;
    if (formData.is_licensed) score += 5;
    if (formData.is_insured) score += 10;
    if (formData.job_size_accepted.length > 0) score += 5;
    if (formData.availability.length > 0) score += 5;
    return score;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="spinner w-10 h-10 border-4 border-[#0FA3B1]/20 border-t-[#0FA3B1] rounded-full" />
      </div>
    );
  }

  const completeness = calculateCompleteness();
  const isVerifiedPro = profile?.verified_pro || false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1F2933] py-6 sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                onClick={() => navigate("/contractor")}
              >
                <ArrowLeft className="w-5 h-5 mr-2" /> Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold text-white">Edit Profile</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#7ED957] hover:bg-[#6BC648] text-white"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Profile Completeness Banner */}
        <Card className={`mb-6 ${completeness >= 80 ? 'border-[#7ED957]/30 bg-[#7ED957]/5' : 'border-amber-300 bg-amber-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {completeness >= 80 ? (
                  <CheckCircle className="w-8 h-8 text-[#7ED957]" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                )}
                <div>
                  <h3 className="font-bold text-[#1F2933]">
                    Profile Completeness: {completeness}%
                  </h3>
                  <p className="text-sm text-[#1F2933]/60">
                    {completeness >= 80 
                      ? "Great job! Your profile is complete." 
                      : "Complete your profile to earn the Verified Pro badge and get more leads."}
                  </p>
                </div>
              </div>
              {isVerifiedPro && (
                <Badge className="bg-[#7ED957] text-white">
                  <Shield className="w-4 h-4 mr-1" /> Verified Pro
                </Badge>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${completeness >= 80 ? 'bg-[#7ED957]' : 'bg-amber-500'}`}
                style={{ width: `${completeness}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Badges Section */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <BadgeSection 
              badges={formData.badges_earned || []} 
              title="Earned Badges"
            />
          </CardContent>
        </Card>

        {/* Business Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#0FA3B1]" />
              Business Information
            </CardTitle>
            <CardDescription>Your business name and contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => handleInputChange("business_name", e.target.value)}
                  placeholder="Your business name"
                  data-testid="business-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="full_name">Contact Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange("full_name", e.target.value)}
                  placeholder="Your name"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="login_email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  Login Email
                </Label>
                <Input
                  id="login_email"
                  type="email"
                  value={user?.email || ""}
                  disabled
                  readOnly
                  className="bg-gray-50 text-gray-600 cursor-not-allowed"
                  data-testid="login-email-input"
                />
                <p className="text-xs text-gray-500">This is your login email and cannot be changed here.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="(512) 555-1234"
                  data-testid="phone-input"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="years_experience">Years of Experience</Label>
                <select
                  id="years_experience"
                  value={formData.years_experience}
                  onChange={(e) => handleInputChange("years_experience", e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white"
                >
                  <option value="">Select experience</option>
                  <option value="0-2">0-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">Business Description</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                placeholder="Tell homeowners about your business, experience, and what makes you stand out..."
                className="min-h-[120px]"
                data-testid="bio-input"
              />
              <p className="text-xs text-[#1F2933]/50">
                {formData.bio.length}/500 characters
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Skills & Services */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#0FA3B1]" />
              Skills & Services *
            </CardTitle>
            <CardDescription>Select all services you offer</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {AVAILABLE_SKILLS.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => toggleArrayItem("skills", skill)}
                  className={`p-3 rounded-lg border text-left text-sm transition-all ${
                    formData.skills.includes(skill)
                      ? "bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]"
                      : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                  }`}
                  data-testid={`skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {formData.skills.includes(skill) && (
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                  )}
                  {skill}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Service Areas */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0FA3B1]" />
              Service Areas *
            </CardTitle>
            <CardDescription>Select counties where you provide services</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {AUSTIN_COUNTIES.map((county) => (
                <button
                  key={county}
                  type="button"
                  onClick={() => toggleArrayItem("service_areas", county)}
                  className={`p-4 rounded-xl border text-center text-sm font-medium transition-all ${
                    formData.service_areas.includes(county)
                      ? "bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]"
                      : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                  }`}
                  data-testid={`county-${county.toLowerCase()}`}
                >
                  {formData.service_areas.includes(county) && (
                    <CheckCircle className="w-4 h-4 inline mr-2" />
                  )}
                  {county} County
                </button>
              ))}
            </div>
            <p className="text-sm text-[#1F2933]/50 mt-3">
              Selected: {formData.service_areas.length} {formData.service_areas.length === 1 ? 'county' : 'counties'}
            </p>
          </CardContent>
        </Card>

        {/* Licensing & Insurance */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0FA3B1]" />
              Licensing & Insurance
            </CardTitle>
            <CardDescription>Verify your credentials to earn Verified Pro status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleInputChange("is_licensed", !formData.is_licensed)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  formData.is_licensed
                    ? "bg-[#7ED957]/10 border-[#7ED957]"
                    : "bg-white border-gray-200 hover:border-[#7ED957]/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    formData.is_licensed ? "bg-[#7ED957] border-[#7ED957]" : "border-gray-300"
                  }`}>
                    {formData.is_licensed && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-[#1F2933]">Licensed</p>
                    <p className="text-sm text-[#1F2933]/60">I have a valid business license</p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleInputChange("is_insured", !formData.is_insured)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  formData.is_insured
                    ? "bg-[#7ED957]/10 border-[#7ED957]"
                    : "bg-white border-gray-200 hover:border-[#7ED957]/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    formData.is_insured ? "bg-[#7ED957] border-[#7ED957]" : "border-gray-300"
                  }`}>
                    {formData.is_insured && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-[#1F2933]">Insured</p>
                    <p className="text-sm text-[#1F2933]/60">I carry liability insurance</p>
                  </div>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Job Preferences */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#0FA3B1]" />
              Job Preferences
            </CardTitle>
            <CardDescription>Set your availability and job size preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="mb-3 block">Job Sizes Accepted</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {JOB_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleArrayItem("job_size_accepted", size)}
                    className={`p-3 rounded-lg border text-center text-sm transition-all ${
                      formData.job_size_accepted.includes(size)
                        ? "bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]"
                        : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-3 block">Availability</Label>
              <div className="grid grid-cols-2 gap-3">
                {AVAILABILITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleArrayItem("availability", opt.value)}
                    className={`p-3 rounded-lg border text-left text-sm transition-all ${
                      formData.availability.includes(opt.value)
                        ? "bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]"
                        : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-3 block">Preferred Contact Method</Label>
              <div className="flex gap-3">
                {["phone", "text", "email"].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => handleInputChange("preferred_contact_method", method)}
                    className={`px-6 py-3 rounded-lg border text-sm font-medium transition-all capitalize ${
                      formData.preferred_contact_method === method
                        ? "bg-[#0FA3B1] text-white border-[#0FA3B1]"
                        : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                    }`}
                  >
                    {method}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Online Reviews */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#0FA3B1]" />
              Online Reviews
            </CardTitle>
            <CardDescription>Link your existing reviews to boost credibility</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: "google", label: "Google" },
                { value: "yelp", label: "Yelp" },
                { value: "thumbtack", label: "Thumbtack" },
                { value: "none", label: "No reviews yet" }
              ].map((source) => (
                <button
                  key={source.value}
                  type="button"
                  onClick={() => handleInputChange("review_source", source.value)}
                  className={`p-3 rounded-lg border text-center text-sm transition-all ${
                    formData.review_source === source.value
                      ? "bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]"
                      : "bg-white border-gray-200 hover:border-[#0FA3B1]/50"
                  }`}
                >
                  {source.label}
                </button>
              ))}
            </div>
            {formData.review_source && formData.review_source !== "none" && (
              <div className="space-y-2">
                <Label htmlFor="review_url">Review Page URL</Label>
                <Input
                  id="review_url"
                  value={formData.review_url}
                  onChange={(e) => handleInputChange("review_url", e.target.value)}
                  placeholder={`Your ${formData.review_source} review page URL`}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/contractor")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#7ED957] hover:bg-[#6BC648] text-white px-8"
            data-testid="save-profile-btn-bottom"
          >
            {saving ? (
              <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
